import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import { AlertBanner } from "@qubit/autoparts";
import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";
import {
  selectNextEmptyBinByPort,
  selectThisWorkstation
} from "~/redux/selectors/workstationsSelectors";

import {
  closeFoodSafetyModal,
  setBinOverrides
} from "./autostorePutaway.slice";

export const FoodSafetyConfigurationModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const ports = useAppSelector(selectThisWorkstation)?.ports ?? [];
  const binInfo = useAppSelector(selectNextEmptyBinByPort);
  const [selectedPorts, setSelectedPorts] = useState<number[]>([]);

  const portClick = useCallback(
    (portId: number) => {
      if (selectedPorts.includes(portId)) {
        setSelectedPorts(selectedPorts.filter((p) => p != portId));
      } else {
        setSelectedPorts([...selectedPorts, portId]);
      }
    },
    [selectedPorts]
  );

  return (
    <Dialog open fullWidth maxWidth="lg">
      <DialogTitle>{t("food safety")}</DialogTitle>
      <Box sx={{ width: "fit-content", mt: 3, mx: "auto" }}>
        <AlertBanner severity="warning">
          {t("whole bins required for food safety items")}
        </AlertBanner>
      </Box>
      <DialogContent>
        <Typography variant="h4" textAlign="center" mt={2}>
          {t(
            "to ensure food safety, please designate one or more ports for whole bins"
          )}
        </Typography>
        <Stack direction="row" gap={2} py={2} justifyContent="center">
          {ports.map((p) => {
            const bin = binInfo[p.portId];
            if (!bin) {
              return;
            }

            const selected = selectedPorts.includes(p.portId);
            const binConfig = bin.autostoreBinConfiguration;
            return (
              <Box
                key={p.portId}
                width="100%"
                maxWidth="400px"
                border="12px solid"
                p={1}
                borderColor={selected ? "primary.main" : "transparent"}
                borderRadius="8px"
                aria-label={`port ${p.portId} selector`}
              >
                <AutostoreBin
                  state="Bin Opened"
                  numberOfColumns={binConfig?.verticalCompartmentCount ?? 1}
                  numberOfRows={binConfig?.horizontalCompartmentCount ?? 1}
                  pickCompartment={null}
                  pickQuantity={""}
                  onBinClick={() => portClick(p.portId)}
                />
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", mb: 2 }}>
        <Button
          variant="subtle"
          size="large"
          onClick={() => dispatch(closeFoodSafetyModal())}
        >
          {t("cancel")}
        </Button>
        <Button
          size="large"
          disabled={!selectedPorts.length}
          onClick={() => dispatch(setBinOverrides(selectedPorts))}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
